import {
  Box,
  Checkbox,
  Menu,
  MenuItem,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import SearchField from "../../../../components/filters/SearchField";
import TenantAvatar from "../../../../components/mssp/TenantAvatar";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";

const TenantMultiSelect = ({
  tenants = [],
  selectedTenantIds = [],
  onSelectionChange,
  onSelectionChanged,
  label = "Tenants",
  hasMultipleSelectedTenants = false,
}) => {
  const colors = useColors();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const menuOpen = Boolean(anchorEl);

  const allTenantsSelected =
    tenants.length > 0 &&
    tenants.every((tenant) => selectedTenantIds.includes(tenant.customer_id));

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setInitialSelectedIds([...selectedTenantIds]);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSearchQuery("");

    const hasChanged =
      initialSelectedIds.length !== selectedTenantIds.length ||
      !initialSelectedIds.every((id) => selectedTenantIds.includes(id));

    if (hasChanged) {
      onSelectionChanged?.(selectedTenantIds);
    }
  };

  const handleCheckboxChange = (tenantId, isChecked) => {
    if (isChecked) {
      onSelectionChange([...selectedTenantIds, tenantId]);
    } else {
      onSelectionChange(selectedTenantIds.filter((id) => id !== tenantId));
    }
  };

  const handleSelectAll = () => {
    if (allTenantsSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(tenants.map((tenant) => tenant.customer_id));
    }
  };

  const filteredTenants = tenants.filter((tenant) =>
    tenant.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getButtonLabel = () => {
    if (tenants.length > 0 && selectedTenantIds.length === tenants.length) {
      return "All Tenants";
    }
    if (selectedTenantIds.length === 1) {
      const tenant = tenants.find(
        (t) => t.customer_id === selectedTenantIds[0]
      );
      return tenant?.company_name || "1 Tenant";
    }
    return `${selectedTenantIds.length} Tenants`;
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: colors.text_secondary, mb: 1, display: "block" }}
      >
        {label}
      </Typography>
      <ToggleButton
        value="tenant"
        selected={hasMultipleSelectedTenants}
        onClick={handleMenuOpen}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          borderRadius: "8px",
          border: `1px solid ${colors.textfield_border}`,
          padding: "8px 12px",
          width: "100%",
          backgroundColor: colors.common_white,
          "&:hover": {
            backgroundColor: colors.grey_8percent,
            border: `1px solid ${colors.textfield_border_hover}`,
          },
          "&.Mui-selected": {
            backgroundColor: colors.common_white,
            border: `1px solid ${colors.textfield_border}`,
            "&:hover": {
              backgroundColor: colors.grey_8percent,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selectedTenantIds.length === 1 ? (
            <>
              <TenantAvatar
                tenant={tenants.find(
                  (t) => t.customer_id === selectedTenantIds[0]
                )}
                size={20}
              />
              <Typography variant="caption">{getButtonLabel()}</Typography>
            </>
          ) : (
            <>
              <SvgToIcon IconLogo={icons.MultiTenantIcon} width="20px" />
              <Typography variant="caption">{getButtonLabel()}</Typography>
            </>
          )}
        </Box>
        <SvgToIcon IconLogo={icons.chevronDownIcon} width="20px" />
      </ToggleButton>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: anchorEl?.offsetWidth, mt: 0.5 },
        }}
      >
        <Box sx={{ padding: "8px 12px" }}>
          <SearchField
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            colors={colors}
            theme={theme}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              onClick={handleSelectAll}
              sx={{
                cursor: "pointer",
                color: colors.text_link,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {allTenantsSelected ? "Clear Selection" : "Select All"}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {filteredTenants.map((tenant) => (
              <MenuItem
                key={tenant.customer_id}
                onClick={() =>
                  handleCheckboxChange(
                    tenant.customer_id,
                    !selectedTenantIds.includes(tenant.customer_id)
                  )
                }
                sx={{
                  padding: "8px 4px",
                  borderRadius: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={selectedTenantIds.includes(tenant.customer_id)}
                    onChange={(e) =>
                      handleCheckboxChange(tenant.customer_id, e.target.checked)
                    }
                    onClick={(e) => e.stopPropagation()}
                    sx={{ padding: 0, marginRight: "8px" }}
                    icon={
                      <SvgToIcon
                        IconLogo={icons.Unchecked}
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                    checkedIcon={
                      <SvgToIcon
                        IconLogo={icons.Checked}
                        style={{ width: "20px", height: "20px" }}
                      />
                    }
                  />
                  <TenantAvatar tenant={tenant} size={20} />
                  <Typography variant="caption">
                    {tenant.company_name}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default TenantMultiSelect;
